import * as _ from 'lodash';
import * as moment from 'moment';
import 'moment/locale/fr';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Component, OnInit, OnDestroy, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { InvoiceViewType } from '@app/core/models/invoices/invoice-view-type.model';
import { InvoiceModel } from '@app/core/models/invoices/invoice.model';
import {
  TableRowAction,
  TableActionIcon,
  TableIndication,
  TableMassAction,
} from '@app/shared/models/table-action-type.model';
import { ModalHelper } from '@app/shared/models/modal-helper.model';
import { TableColumnsConfigKey } from '@app/core/models/configuration/table-columns-config-key.model';
import { InvoiceService } from '@app/core/services/invoice.service';
import { FileDownloadService } from '@app/core/services/filedownload.service';
import { ModalComponent } from '@app/shared/components/modal/modal.component';
import { ProgressComponent } from '@app/shared/components/progress/progress.component';
import { HttpCancelService } from '@app/core/services/http-cancel.service';
import { WorkflowStageValidationComponent } from '@app/features/workflow-stage-validation/workflow-stage-validation.component';
import { WorkflowStageValidationModel } from '@app/core/models/workflows/workflow-stage-validation.model';
import { v4 as uuidv4 } from 'uuid';
moment.locale('fr');

@Component({
  selector: 'app-invoice-list-voucher-workflow',

  templateUrl: './invoice-list-voucher-workflow.component.html',
  styleUrls: ['./invoice-list-voucher-workflow.component.scss'],
})
export class InvoiceListVoucherWorkflowComponent implements OnInit, OnDestroy {
  invoices: InvoiceModel[];
  data: InvoiceModel[] = [];
  tableActions: TableRowAction[] = [];
  tableMassActions: TableMassAction[] = [];
  tableIndications: TableIndication[] = [];
  unsubscribe$: Subject<boolean> = new Subject<boolean>();
  configKey: string = TableColumnsConfigKey.INVOICEVOUCHERWORKFLOW;
  exportFileName: string;
  private massDownloadRequestSubject$ = new BehaviorSubject<string>(null);
  public massDownloadRequest$ = this.massDownloadRequestSubject$.asObservable();
  progressDialog: MatDialogRef<ProgressComponent>;

  title: string;

  public onFiltersCleared: EventEmitter<void> = new EventEmitter<void>();
  public onExportExcelCaller: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    private invoiceService: InvoiceService,
    private fileDownloadService: FileDownloadService,
    private route: ActivatedRoute,
    private router: Router,
    private httpCancelService: HttpCancelService,
    public dialog: MatDialog
  ) {
    this.exportFileName = 'Factures_A_Valider';
    this.title = 'FACTURES A VALIDER';
  }

  ngOnInit(): void {
    this.route.data.subscribe((data) => (this.invoices = data.invoices));
    this.data = this.invoices;
    this.tableActions.push(
      new TableRowAction(
        TableActionIcon.ZOOM,
        'Voir le détail de la facture',
        this.onZoom
      )
    );
    this.tableActions.push(
      new TableRowAction(
        TableActionIcon.PDF,
        'Ouvrir la facture',
        this.onOpenPdf
      )
    );
    this.tableActions.push(
      new TableRowAction(
        TableActionIcon.PROCESS,
        'Valider la facture',
        this.onWorkflowStageValidation
      )
    );
    this.tableActions.push(
      new TableRowAction(
        TableActionIcon.VOUCHER,
        'Télécharger le bon à payer',
        this.onVoucherDownload
      )
    );
    this.tableMassActions.push(
      new TableMassAction(
        TableActionIcon.PROCESS,
        'Valider les factures',
        this.onWorkflowStagesValidation
      )
    );
    this.tableMassActions.push(
      new TableMassAction(
        TableActionIcon.VOUCHER,
        'Télécharger les bons à payer',
        this.onMassVouchersDownload
      )
    );
    this.tableIndications.push(
      new TableIndication(
        TableActionIcon.ERROR,
        "L'association Référence 1 et société n'est pas conforme",
        (invoice: InvoiceModel) => invoice.SupplierHasWarning
      )
    );

    this.massDownloadRequest$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((requestId: string) => {
        if (_.isNil(requestId)) {
          if (!_.isNil(this.progressDialog)) {
            this.progressDialog.close(false);
          }
          return;
        }

        const progressModal = new ModalHelper().getProgressConfig(requestId);
        this.progressDialog = this.dialog.open(
          ProgressComponent,
          progressModal
        );

        this.progressDialog
          .afterClosed()
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe((isSuccess: boolean) => {
            this.massDownloadRequestSubject$.next(null);
            this.progressDialog = null;
            if (!isSuccess) {
              this.httpCancelService.cancelPendingRequests();
            }
          });
      });
  }

  onZoom = (data: InvoiceModel) => {
    this.router.navigate([
      'invoices',
      InvoiceViewType.VOUCHERWORKFLOW,
      'detail',
      data.Id,
    ]);
  };

  onOpenPdf = (data: InvoiceModel) => {
    window.open(
      `invoices/${InvoiceViewType.VOUCHERWORKFLOW}/detail-pdf/${data.Id}`,
      '_blank'
    );
  };

  onVoucherDownload = (invoice: InvoiceModel) => {
    this.invoiceService
      .getAttachmentVoucher(invoice.Id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (blobData: Blob) => {
          let date = moment(invoice.IssuedAt).format('YYYYMMDo');
          this.fileDownloadService.downloadBlob(
            blobData,
            `${invoice.SupplierCompanyName}_${invoice.Number}_${date}.pdf`,
            'application/pdf'
          );
        },
        (_) => {
          const modalErreurConfig = new ModalHelper().getAlertConfig(
            `Une erreur est survenue lors du téléchargement.`
          );
          this.dialog.open(ModalComponent, modalErreurConfig);
        }
      );
  };

  onWorkflowStageValidation = (data: InvoiceModel) => {
    const workflowStageValidationConfig =
      new ModalHelper().getWorkflowStageValidationConfig(
        data.Id,
        data.Number,
        'facture',
        null,
        null
      );
    workflowStageValidationConfig.data.ApproveWorkflowStage = (
      invoiceId: string,
      validation: WorkflowStageValidationModel
    ) => this.invoiceService.approveCurrentWorkflowStage(invoiceId, validation);
    const dialog = this.dialog.open(
      WorkflowStageValidationComponent,
      workflowStageValidationConfig
    );

    return dialog
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isSuccess: boolean) => {
        if (isSuccess) {
          this.data = this.data.filter((i) => i.Id != data.Id);
        }
      });
  };

  onMassVouchersDownload = (selectedIds: string[]) => {
    if (selectedIds.length > 0) {
      let massDownloadRequest = uuidv4();
      this.massDownloadRequestSubject$.next(massDownloadRequest);
      this.invoiceService
        .getAttachmentVouchers(selectedIds, massDownloadRequest)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          (blobData: Blob) => {
            this.massDownloadRequestSubject$.next(null);
            let localDate = new Date().toLocaleDateString();
            this.fileDownloadService.downloadBlob(
              blobData,
              `${this.configKey}_${localDate}.zip`,
              'application/zip'
            );
          },
          (_) => {
            this.massDownloadRequestSubject$.next(null);
            const modalErreurConfig = new ModalHelper().getAlertConfig(
              `Une erreur est survenue lors du téléchargement.`
            );
            this.dialog.open(ModalComponent, modalErreurConfig);
          }
        );
    }
  };

  onWorkflowStagesValidation = (invoicesId: string[]) => {
    const workflowStageValidationConfig =
      new ModalHelper().getWorkflowStagesValidationConfig(
        invoicesId,
        'facture'
      );
    workflowStageValidationConfig.data.ApproveWorkflowsStage = (
      invoicesId: string[],
      validation: WorkflowStageValidationModel
    ) =>
      this.invoiceService.approveCurrentWorkflowsStage(invoicesId, validation);
    const dialog = this.dialog.open(
      WorkflowStageValidationComponent,
      workflowStageValidationConfig
    );

    return dialog
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isSuccess: boolean) => {
        if (isSuccess) {
          this.data = this.data.filter((i) => !invoicesId.includes(i.Id));
        }
      });
  };

  ngOnDestroy() {
    this.unsubscribe$.next(true);
  }

  onClearFilters = (): void => {
    this.onFiltersCleared.next();
  };

  exportAsExcel(): void {
    this.onExportExcelCaller.next();
  }
}

